/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useState } from 'react';
import { Autocomplete, FormControl, TextField } from '@mui/material';
import { useLang } from '~/hooks/useLang';
import { useHookstate } from '@hookstate/core';
import { authFetch } from '~/services/fetch';

interface InspectionItemResponse {
  readonly id: number;
  readonly name: string;
}

interface FilterAutocompleteInspectionItemMultipleProps {
  readonly multiple: true;
  readonly onInspectionItemChange: (value: readonly number[]) => void;
  readonly inspectionItemIds?: readonly number[];
  readonly inspectionItemId?: never;
  readonly disabled?: boolean;
  readonly size?: 'small' | 'medium';
}

interface FilterAutocompleteInspectionItemSingleProps {
  readonly multiple?: false;
  readonly onInspectionItemChange: (value: number | null) => void;
  readonly inspectionItemId?: number | null;
  readonly inspectionItemIds?: never;
  readonly disabled?: boolean;
  readonly size?: 'small' | 'medium';
}

type FilterAutocompleteInspectionItemProps =
  | FilterAutocompleteInspectionItemMultipleProps
  | FilterAutocompleteInspectionItemSingleProps;

export default function FilterAutocompleteInspectionItem(props: FilterAutocompleteInspectionItemProps) {
  const { translate } = useLang();
  const { disabled, size = 'small', multiple = false } = props;
  const [options, setOptions] = useState<readonly InspectionItemResponse[]>([]);
  const [multiValue, setMultiValue] = useState<readonly InspectionItemResponse[]>([]);
  const [singleValue, setSingleValue] = useState<InspectionItemResponse | null>(null);
  const loading = useHookstate(false);

  useEffect(() => {
    loading.set(true);
    authFetch({
      url: '/inspection-item',
      method: 'GET',
    }).then((response) => {
      setOptions(response.data);
    }).finally(() => {
      loading.set(false);
    });
  }, []);

  useEffect(() => {
    if (multiple) {
      const ids = (props as FilterAutocompleteInspectionItemMultipleProps).inspectionItemIds ?? [];
      if (ids.length === 0) setMultiValue([]);
    } else {
      const id = (props as FilterAutocompleteInspectionItemSingleProps).inspectionItemId;
      if (!id) setSingleValue(null);
    }
  }, [multiple ? (props as FilterAutocompleteInspectionItemMultipleProps).inspectionItemIds : (props as FilterAutocompleteInspectionItemSingleProps).inspectionItemId]);

  if (multiple) {
    const { onInspectionItemChange } = props as FilterAutocompleteInspectionItemMultipleProps;
    return (
      <FormControl fullWidth size={size}>
        <Autocomplete
          multiple
          id="filter-inspection-item"
          options={options}
          disabled={disabled}
          value={multiValue as InspectionItemResponse[]}
          noOptionsText={translate('No options')}
          clearText={translate('Clear')}
          size={size}
          loading={loading.get()}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          getOptionLabel={(option) => option.name ?? ''}
          onChange={(_, items) => {
            setMultiValue(items);
            onInspectionItemChange(items.map((item) => item.id));
          }}
          renderInput={(params) => (
            <TextField {...params} label={translate('Item')} />
          )}
        />
      </FormControl>
    );
  }

  const { onInspectionItemChange } = props as FilterAutocompleteInspectionItemSingleProps;
  return (
    <FormControl fullWidth size={size}>
      <Autocomplete
        id="filter-inspection-item"
        options={options}
        disabled={disabled}
        value={singleValue}
        noOptionsText={translate('No options')}
        clearText={translate('Clear')}
        size={size}
        loading={loading.get()}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        getOptionLabel={(option) => option.name ?? ''}
        onChange={(_, item) => {
          setSingleValue(item);
          onInspectionItemChange(item?.id ?? null);
        }}
        renderInput={(params) => (
          <TextField {...params} label={translate('Item')} />
        )}
      />
    </FormControl>
  );
}
